import React, { useState, useEffect } from "react";
import CloudBackground from "../community/shared/components/CloudBackground";
import PButton from "../../ui/input/PButton";
import GiftPersonalizationForm from "./GiftPersonalizationForm";
import getStaticAssetUrl from "../../../utils/assetUtils";

type GiftPackage = {
  id: string;
  setupCount: number;
  totalPrice: number;
  pricePerSetup: number;
  sku: string;
  eyebrowHeader?: string;
};

type GiftPersonalizationData = {
  payerFullName: string;
  payerEmail: string;
  payerPhone: string;
  recipientFullName: string;
  recipientEmail: string;
  recipientPhone: string;
  recipientLocation: string;
  recipientAge: string;
  relationship: string;
  personalizedMessage: string;
};

type GiftFlowStep =
  | "landing"
  | "packageSelection"
  | "personalization"
  | "tooYoungInfo"
  | "unsupportedLocationInfo";

type GiftLandingPageProps = {
  onGiftPurchase?: (
    packageData: GiftPackage,
    personalizationData: GiftPersonalizationData,
  ) => void;
  giftPurchaseUrl?: string;
  startingStep?: GiftFlowStep;
  selectedPackageId?: string;
  initialPersonalizationData?: GiftPersonalizationData;
};

// PackageCard component moved outside to avoid re-renders
const PackageCard: React.FC<{
  pkg: GiftPackage;
  selectedPackage: string | null;
  onPackageClick: (pkg: GiftPackage) => void;
}> = ({ pkg, selectedPackage, onPackageClick }) => {
  const isSelected = selectedPackage === pkg.id;

  const getPackageStatement = (packageId: string) => {
    switch (packageId) {
      case "1-setup":
        return {
          title: "Save the Date:",
          description:
            'You\'re responding "yes" on their behalf. The plus one is TBD.',
        };
      case "3-setups":
        return {
          title: "The Plus One:",
          description:
            "Like buying them a drink at the bar except this time, it actually leads somewhere.",
        };
      case "5-setups":
        return {
          title: "The Rehearsal Dinner:",
          description:
            "You're not just rooting for them; you're officially making it happen.",
        };
      case "8-setups":
        return {
          title: "The Maid of Honor / Best Man:",
          description:
            "Peak wingman energy. They'll be thanking you in their vows.",
        };
      default:
        return null;
    }
  };

  const statement = getPackageStatement(pkg.id);
  const showStatement = Boolean(isSelected && statement);

  const cardBaseClasses =
    "group relative w-full cursor-pointer focus:outline-none transition-transform duration-200 ease-out";

  const topBorderClasses = isSelected
    ? "relative z-10 p-[1.2px] rounded-[28px] bg-gradient-to-r from-[#F3E48A] to-[#84C5FF] shadow-[0_20px_42px_rgba(8,27,52,0.28)]"
    : "relative z-10 rounded-[28px] shadow-[0_20px_42px_rgba(8,27,52,0.22)]";

  return (
    <div
      className={cardBaseClasses}
      onClick={() => onPackageClick(pkg)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          onPackageClick(pkg);
        }
      }}
      role="button"
      tabIndex={0}
      aria-pressed={isSelected}
    >
      <div className={topBorderClasses}>
        <div
          className="rounded-[26px] overflow-hidden text-white flex items-start justify-between gap-5 px-7 pt-8 pb-[26px]"
          style={{
            backdropFilter: "blur(10px)",
            background:
              "linear-gradient(180deg, rgba(13,51,88,0.88) 0%, rgba(10,40,72,0.72) 100%)",
          }}
        >
          <div className="flex flex-col gap-1 items-start">
            {pkg.eyebrowHeader && (
              <div
                className="text-[11px] uppercase font-seriously-nostalgic text-most-popular"
                style={{ letterSpacing: "0.28em" }}
              >
                {pkg.eyebrowHeader}
              </div>
            )}
            <div className="text-[28px] leading-tight font-seriously-nostalgic">
              {pkg.setupCount} {pkg.setupCount > 1 ? "setups" : "setup"}
            </div>
          </div>

          <div className="flex flex-col items-end">
            <div className="text-[21px] font-semibold leading-tight">
              ${pkg.totalPrice.toFixed(2)} total
            </div>
            <div className="text-[12px] text-white/80 leading-tight">
              ${pkg.pricePerSetup.toFixed(2)}/setup
            </div>
          </div>
        </div>
      </div>

      {showStatement && statement && (
        <div
          className="relative -mt-10 rounded-[28px] text-white shadow-[0_18px_36px_rgba(6,24,42,0.24)]"
          style={{
            backdropFilter: "blur(10px)",
          }}
        >
          <div
            className="rounded-[28px] px-7 pt-12 pb-6"
            style={{
              background:
                "linear-gradient(180deg, rgba(124,200,234,0.58) 0%, rgba(57,119,155,0.40) 100%)",
            }}
          >
            <div className="text-sm leading-relaxed">
              <span className="font-bold">{statement.title}</span>&nbsp;
              {statement.description}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Internal Info Components
const TooYoungInfoView: React.FC<{ onBack: () => void }> = ({ onBack }) => {
  const [backgroundStyle, setBackgroundStyle] = useState<React.CSSProperties>(
    {},
  );

  useEffect(() => {
    const updateBackground = () => {
      const isMobile = window.innerWidth < 768; // md breakpoint
      const imageUrl = isMobile
        ? getStaticAssetUrl("assets/images/too_young_gift_info_mobile.png")
        : getStaticAssetUrl("assets/images/too_young_gift_info.png");

      setBackgroundStyle({
        backgroundImage: `url(${imageUrl})`,
      });
    };

    updateBackground();
    window.addEventListener("resize", updateBackground);
    return () => window.removeEventListener("resize", updateBackground);
  }, []);

  return (
    <div
      className="min-h-screen bg-cover bg-center bg-no-repeat flex flex-col relative"
      style={backgroundStyle}
    >
      <div className="absolute top-8 left-8">
        <PButton
          onClick={onBack}
          kind="secondary"
          className="bg-white/20 backdrop-blur-md border border-white/40 text-white hover:bg-white/30 px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm rounded-full shadow-lg transition-all"
        >
          ← Back
        </PButton>
      </div>
    </div>
  );
};

const UnsupportedLocationInfoView: React.FC<{ onBack: () => void }> = ({
  onBack,
}) => {
  const [backgroundStyle, setBackgroundStyle] = useState<React.CSSProperties>(
    {},
  );

  useEffect(() => {
    const updateBackground = () => {
      const isMobile = window.innerWidth < 768; // md breakpoint
      const imageUrl = isMobile
        ? getStaticAssetUrl(
            "assets/images/unsupported_location_info_mobile.png",
          )
        : getStaticAssetUrl("assets/images/unsupported_location_info.png");

      setBackgroundStyle({
        backgroundImage: `url(${imageUrl})`,
      });
    };

    updateBackground();
    window.addEventListener("resize", updateBackground);
    return () => window.removeEventListener("resize", updateBackground);
  }, []);

  return (
    <div
      className="min-h-screen bg-cover bg-center bg-no-repeat flex flex-col relative"
      style={backgroundStyle}
    >
      <div className="absolute top-8 left-8">
        <PButton
          onClick={onBack}
          kind="secondary"
          className="bg-white/20 backdrop-blur-md border border-white/40 text-white hover:bg-white/30 px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm rounded-full shadow-lg transition-all"
        >
          ← Back
        </PButton>
      </div>

      <div className="flex-1 flex items-end justify-center pb-20">
        <PButton
          href="https://www.instagram.com/sitch.matchmaking/"
          kind="primary"
          className="!bg-black hover:!bg-[#111111] !text-white !border-black px-6 py-3 text-sm font-medium rounded-full shadow-xl transform hover:scale-105 transition-all duration-300 flex items-center gap-2"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="currentColor"
            className="flex-shrink-0"
          >
            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
          </svg>
          Follow us on Instagram
        </PButton>
      </div>
    </div>
  );
};

const GiftLandingPage: React.FC<GiftLandingPageProps> = ({
  onGiftPurchase,
  giftPurchaseUrl,
  startingStep = "landing",
  selectedPackageId,
  initialPersonalizationData,
}) => {
  // Add custom animations and fancy fonts
  React.useEffect(() => {
    // Preload the Seriously Nostalgic font
    const preloadLink = document.createElement("link");
    preloadLink.rel = "preload";
    preloadLink.href = "/assets/fonts/SeriouslyNostalgicFn-Regular.otf";
    preloadLink.as = "font";
    preloadLink.type = "font/otf";
    preloadLink.crossOrigin = "anonymous";
    document.head.appendChild(preloadLink);

    const style = document.createElement("style");
    style.textContent = `
      @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,600;0,700;0,800;0,900;1,400;1,500;1,600;1,700;1,800;1,900&family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;0,700;1,300;1,400;1,500;1,600;1,700&family=Crimson+Text:ital,wght@0,400;0,600;0,700;1,400;1,600;1,700&family=Libre+Baskerville:ital,wght@0,400;0,700;1,400&display=swap');
      
      @font-face {
        font-family: 'Seriously Nostalgic';
        src: url('/assets/fonts/SeriouslyNostalgicFn-Regular.otf') format('opentype');
        font-display: swap;
        font-weight: normal;
        font-style: normal;
      }
      
      @keyframes float {
        0%, 100% { transform: translateY(0px) rotate(0deg); }
        50% { transform: translateY(-10px) rotate(2deg); }
      }
      @keyframes glow {
        0%, 100% { box-shadow: 0 0 20px rgba(168, 85, 247, 0.3); }
        50% { box-shadow: 0 0 40px rgba(168, 85, 247, 0.6), 0 0 60px rgba(236, 72, 153, 0.3); }
      }
      @keyframes shimmer {
        0% { background-position: -200% 0; }
        100% { background-position: 200% 0; }
      }
      .animate-float { animation: float 6s ease-in-out infinite; }
      .animate-glow { animation: glow 3s ease-in-out infinite; }
      .animate-shimmer { 
        background: linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent);
        background-size: 200% 100%;
        animation: shimmer 3s ease-in-out infinite;
      }
      .font-playfair { font-family: 'Playfair Display', serif; }
      .font-cormorant { font-family: 'Cormorant Garamond', serif; }
      .font-crimson { font-family: 'Crimson Text', serif; }
      .font-libre { font-family: 'Libre Baskerville', serif; }
      .font-seriously-nostalgic { 
        font-family: 'Seriously Nostalgic', 'Playfair Display', 'Times New Roman', serif !important;
        font-weight: normal;
        font-style: normal;
      }
      .text-most-popular {
        background: linear-gradient(90deg, #F7ECA8 0%, #B0CAE8 60%, #EBF3FF 100%);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
        color: transparent;
        letter-spacing: 0.24em;
      }
    `;
    document.head.appendChild(style);

    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
      if (document.head.contains(preloadLink)) {
        document.head.removeChild(preloadLink);
      }
    };
  }, []);

  // Load saved state from localStorage on component mount
  const [currentStep, setCurrentStep] = useState<GiftFlowStep>(() => {
    if (typeof window !== "undefined") {
      const savedStep = localStorage.getItem("giftFlowStep");
      if (
        savedStep &&
        [
          "landing",
          "packageSelection",
          "personalization",
          "tooYoungInfo",
          "unsupportedLocationInfo",
        ].includes(savedStep)
      ) {
        return savedStep as GiftFlowStep;
      }
    }
    return startingStep;
  });

  const [selectedPackage, setSelectedPackage] = useState<string | null>(() => {
    if (typeof window !== "undefined") {
      const savedPackage = localStorage.getItem("giftSelectedPackage");
      return savedPackage || selectedPackageId || null;
    }
    return selectedPackageId || null;
  });

  const [_personalizationData, setPersonalizationData] =
    useState<GiftPersonalizationData | null>(() => {
      if (typeof window !== "undefined") {
        const savedData = localStorage.getItem("giftPersonalizationData");
        if (savedData) {
          try {
            return JSON.parse(savedData);
          } catch (e) {
            // Invalid JSON, ignore
          }
        }
      }
      return initialPersonalizationData || null;
    });

  const [personalizationStep, setPersonalizationStep] = useState<1 | 2>(() => {
    if (typeof window !== "undefined") {
      const savedStep = localStorage.getItem("giftPersonalizationStep");
      if (savedStep === "1" || savedStep === "2") {
        return parseInt(savedStep, 10) as 1 | 2;
      }
    }
    return 1;
  });

  // Save state to localStorage whenever it changes
  React.useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem("giftFlowStep", currentStep);
    }
  }, [currentStep]);

  React.useEffect(() => {
    if (typeof window !== "undefined") {
      if (selectedPackage) {
        localStorage.setItem("giftSelectedPackage", selectedPackage);
      } else {
        localStorage.removeItem("giftSelectedPackage");
      }
    }
  }, [selectedPackage]);

  React.useEffect(() => {
    if (typeof window !== "undefined") {
      if (_personalizationData) {
        localStorage.setItem(
          "giftPersonalizationData",
          JSON.stringify(_personalizationData),
        );
      } else {
        localStorage.removeItem("giftPersonalizationData");
      }
    }
  }, [_personalizationData]);

  React.useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem(
        "giftPersonalizationStep",
        personalizationStep.toString(),
      );
    }
  }, [personalizationStep]);

  const packages: GiftPackage[] = [
    {
      id: "1-setup",
      setupCount: 1,
      totalPrice: 34.99,
      pricePerSetup: 34.99,
      sku: "SITCH_SETUP_01_V2",
      eyebrowHeader: "🚨 LIMITED TIME OFFER",
    },
    {
      id: "3-setups",
      setupCount: 3,
      totalPrice: 89.99,
      pricePerSetup: 29.99,
      sku: "SITCH_SETUP_03",
    },
    {
      id: "5-setups",
      setupCount: 5,
      totalPrice: 124.99,
      pricePerSetup: 24.99,
      sku: "SITCH_SETUP_05",
      eyebrowHeader: "MOST POPULAR",
    },
    {
      id: "8-setups",
      setupCount: 8,
      totalPrice: 159.99,
      pricePerSetup: 19.99,
      sku: "SITCH_SETUP_08",
    },
  ];

  const handleGiftSetupsClick = () => {
    setCurrentStep("packageSelection");
  };

  const handleBackClick = () => {
    setCurrentStep("landing");
  };

  const handlePackageClick = (pkg: GiftPackage) => {
    setSelectedPackage(pkg.id);
  };

  const handlePackageSelectedClick = () => {
    setCurrentStep("personalization");
  };

  const handlePersonalizationFormSubmit = (data: GiftPersonalizationData) => {
    const selected = packages.find((pkg) => pkg.id === selectedPackage);
    if (selected) {
      setPersonalizationData(data);
      onGiftPurchase?.(selected, data);
    }
  };

  const handleBackFromPersonalization = () => {
    setCurrentStep("packageSelection");
  };

  const handleBackFromInfoPages = () => {
    setCurrentStep("personalization");
  };

  // Personalization Form View
  if (currentStep === "personalization") {
    const selectedPkg = packages.find((pkg) => pkg.id === selectedPackage);
    if (!selectedPkg) {
      // If no package selected, go back to package selection
      setCurrentStep("packageSelection");
      return null;
    }

    return (
      <GiftPersonalizationForm
        selectedPackage={selectedPkg}
        onSubmit={handlePersonalizationFormSubmit}
        onBackClick={handleBackFromPersonalization}
        giftPurchaseUrl={giftPurchaseUrl}
        isLoading={false}
        activeStep={personalizationStep}
        onStepChange={setPersonalizationStep}
        onTooYoungInfoClick={() => setCurrentStep("tooYoungInfo")}
        onUnsupportedLocationInfoClick={() =>
          setCurrentStep("unsupportedLocationInfo")
        }
      />
    );
  }

  // Too Young Info View
  if (currentStep === "tooYoungInfo") {
    return <TooYoungInfoView onBack={handleBackFromInfoPages} />;
  }

  // Unsupported Location Info View
  if (currentStep === "unsupportedLocationInfo") {
    return <UnsupportedLocationInfoView onBack={handleBackFromInfoPages} />;
  }

  // Package Selection View
  if (currentStep === "packageSelection") {
    return (
      <CloudBackground>
        {/* Floating background elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-purple-500/5 rounded-full blur-3xl animate-pulse" />
          <div className="absolute top-3/4 right-1/4 w-48 h-48 bg-pink-500/5 rounded-full blur-2xl animate-pulse delay-1000" />
          <div className="absolute top-1/2 left-3/4 w-32 h-32 bg-blue-500/5 rounded-full blur-xl animate-pulse delay-2000" />
        </div>

        <div className="relative min-h-screen px-6 py-4 sm:py-12 pb-8">
          {/* Back Button */}
          <div className="relative top-4 left-0 sm:absolute sm:top-8 sm:left-8 mb-4 sm:mb-0">
            <PButton
              onClick={handleBackClick}
              kind="secondary"
              className="bg-white/20 backdrop-blur-sm border-white/50 text-white hover:bg-white/30 px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm"
            >
              ← Back
            </PButton>
          </div>

          {/* Main Content - centered with proper spacing */}
          <div className="text-center max-w-4xl mx-auto flex flex-col items-center justify-center py-4 sm:py-8 mt-4 sm:mt-0">
            {/* Main Heading */}
            <div className="relative mb-8">
              <h1 className="font-seriously-nostalgic text-[42px] sm:text-[52px] md:text-[64px] lg:text-[68px] leading-[0.9] tracking-tight text-white px-1">
                Give the gift of love!
              </h1>
            </div>

            {/* Subtitle - Narrower */}
            <div className="relative mb-14">
              <p className="text-base sm:text-lg md:text-xl text-white/90 max-w-[460px] mx-auto leading-relaxed font-light">
                Our matchmakers set up your recipient based on real
                compatibility — no swiping, just thoughtful intros to people
                worth meeting.
              </p>
            </div>

            {/* Package Options - Vertical Layout */}
            <div className="relative w-full max-w-[440px] mx-auto mb-14">
              <div className="flex flex-col gap-7">
                {packages.map((pkg, index) => (
                  <div
                    key={pkg.id}
                    className="animate-fade-in transform transition-all duration-300"
                    style={{
                      animationDelay: `${index * 150}ms`,
                      animationDuration: "520ms",
                      animationFillMode: "both",
                    }}
                  >
                    <PackageCard
                      pkg={pkg}
                      selectedPackage={selectedPackage}
                      onPackageClick={handlePackageClick}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* CTA Button - Narrower */}
            <div className="mt-8">
              <PButton
                onClick={handlePackageSelectedClick}
                kind="primary"
                disabled={!selectedPackage}
                className={`px-4 py-2 sm:px-6 sm:py-2.5 text-xs sm:text-sm font-medium rounded-full shadow-lg transform transition-all duration-200 ease-out border-0 outline-0 ring-0 focus:outline-0 focus:ring-0 focus:border-0 !border-0 !outline-0 !ring-0 ${
                  selectedPackage
                    ? "!bg-black hover:!bg-[#111111] !text-white hover:scale-[1.02] !border-black"
                    : "cursor-not-allowed opacity-50 !bg-black !text-white/60 !border-black"
                }`}
              >
                Continue
              </PButton>

              {!selectedPackage && (
                <p className="text-white/60 text-sm mt-4">
                  Please select a package above to continue
                </p>
              )}
            </div>
          </div>
        </div>
      </CloudBackground>
    );
  }

  // Landing View (default) - using custom entry point image
  return (
    <div className="min-h-screen relative overflow-auto">
      {/* Custom background image with desktop and mobile variants */}
      <div className="absolute inset-0 pointer-events-none -z-10">
        <picture className="block h-full w-full">
          <source
            media="(max-width: 768px)"
            srcSet={getStaticAssetUrl(
              "assets/images/entry_point_the_sell_mobile.png",
            )}
          />
          <img
            src={getStaticAssetUrl("assets/images/entry_point_the_sell.png")}
            alt=""
            className="h-full w-full object-cover"
          />
        </picture>
      </div>

      {/* Subtle dark overlay for better button visibility if needed */}
      <div className="absolute inset-0 bg-black/10 pointer-events-none -z-10" />

      <div className="relative min-h-screen">
        {/* CTA Button positioned relative to hero text */}
        <div className="absolute left-1/2 top-[62%] sm:top-[66%] md:top-[67%] -translate-x-1/2 w-full max-w-[252px] sm:max-w-[210px] md:max-w-[224px] px-6 z-10">
          <PButton
            onClick={handleGiftSetupsClick}
            kind="primary"
            className="!bg-black hover:!bg-[#111111] !text-white !border-black w-full px-4 py-2 sm:px-6 sm:py-3 text-sm sm:text-base font-medium rounded-full shadow-lg transform hover:scale-[1.02] transition-all duration-200 ease-out border-0 outline-0 ring-0 focus:outline-0 focus:ring-0 focus:border-0 !border-0 !outline-0 !ring-0"
          >
            Gift setups
          </PButton>
        </div>
      </div>
    </div>
  );
};

export default GiftLandingPage;
